// carpets.ts — Dedicated carpet regime (book REGLES_DEVIS §6).
//
// Tufted carpets (MOQ-1202..1213): all-in rate table by tiers of cumulated area (MOQ_TIERS =
// strict upper bounds), sublimation and straight cutting included; only complex cutting (CUT-1503)
// adds +15 €/m². Outside the standard tiers. Needle-punched Event (MOQ-1201) has isMoq=false and
// follows the STANDARD regime (base price + tiers). Each carpet has its own minimums (min_flat_fee,
// min_unit_price €/m²).

import { CARPETS, MOQ_TIERS } from "../../data";
import { engineMessage } from "../core/messages";
import type { Carpet } from "../../models/data.model";
import { type QuoteLine, num, findMat } from "../core/core";
import { compPvOf } from "../catalogue/base-price";
import { ACTIVE_CATALOGUE, type Catalogue } from "../catalogue/catalogue-source";

/**
 * Tufted carpet by reference, or undefined (e.g. MOQ-1201 Event, outside the rate table).
 * @param ref - Material reference to look up.
 * @returns The carpet, or `undefined` when the reference is not in the tufted rate table.
 */
export function findMoq(ref: string | undefined): Carpet | undefined {
  return CARPETS.find((m) => m.ref === ref);
}

/**
 * Tier index for a cumulated area (MOQ_TIERS = UPPER bounds, strict comparison).
 * The last tier (bound 999999) catches everything else. E.g. area 10 → tier 1, 600 → last.
 * @param area - Cumulated area in m².
 * @returns Index of the matching tier.
 * @throws If the MOQ tier referential is empty
 */
export function moqTierIdx(area: number): number {
  if (!MOQ_TIERS.length) {
    throw new Error(engineMessage("engineMoqTiersEmpty"));
  }
  for (let i = 0; i < MOQ_TIERS.length; i++) {
    if (area < MOQ_TIERS[i]! || i === MOQ_TIERS.length - 1) return i;
  }
  return MOQ_TIERS.length - 1;
}

/**
 * Tufted rate table price €/m² for a given cumulated area.
 * @param moq - Tufted carpet.
 * @param area - Cumulated area in m².
 * @returns The rate-table sale price €/m² for the tier.
 */
export function moqPriceM2(moq: Carpet, area: number): number {
  const idx = moqTierIdx(area);
  const pv = moq.pv[idx];
  if (pv == null || !Number.isFinite(Number(pv))) {
    throw new Error(
      engineMessage("engineMoqIncomplete", { ref: moq.ref, tier: idx, tiers: MOQ_TIERS.length }),
    );
  }
  return num(pv);
}

/**
 * Carpet cutting surcharge: only complex cutting (CUT-1503) adds +15 €/m².
 * @param cutCode - Cut reference of the line.
 * @returns The surcharge in €/m² (0 when the cut is not the complex one).
 */
export function moqCutSurcharge(cutCode: string | undefined): number {
  return cutCode === "CUT-1503" ? 15 : 0;
}

/** Carpet price of a line, with its own minimums. */
export interface CarpetPrice {
  /** Base sale price €/m² (tufted rate table + cutting surcharge, or bare + printing for Event). */
  basePvM2: number;
  /** Minimum material flat fee (€). */
  minFlatFee: number;
  /** Minimum unit price (€/m²) specific to the carpet. */
  minUnitM2: number;
  /** true for a tufted carpet (rate table), false for the Event fallback regime. */
  tufted: boolean;
}

/**
 * Price €/m² of a carpet line for a given cumulated area (before client discount and forcing).
 * Tufted: table[tier] + complex cutting surcharge. "Event" fallback (isMoq material with no rate
 * table, inactive in current data): bare material + printing + cutting surcharge.
 * @param line - Quote line carrying the carpet.
 * @param cumulatedArea - Cumulated area of the carpet in m² (selects the tier).
 * @param cat - Catalogue to read materials and prints from.
 * @returns Base price and own minimums of the carpet.
 */
export function carpetPriceM2(line: QuoteLine, cumulatedArea: number, cat: Catalogue = ACTIVE_CATALOGUE): CarpetPrice {
  const materialEntry = findMat(line.material, cat);
  const moq = findMoq(line.material);
  const surcharge = moqCutSurcharge(line.cut);
  if (moq) {
    return {
      basePvM2: moqPriceM2(moq, cumulatedArea) + surcharge,
      minFlatFee: num(moq.min_flat_fee),
      minUnitM2: num(moq.min_unit_price),
      tufted: true,
    };
  }
  // Event fallback: bare material + selected printing (an isMoq material without a rate table).
  const barePv = num(materialEntry?.pv) + (line.print ? compPvOf(cat.prints, line.print) : 0);
  return {
    basePvM2: barePv + surcharge,
    minFlatFee: num(materialEntry?.min_flat_fee),
    minUnitM2: num(materialEntry?.min_unit_price),
    tufted: false,
  };
}
